import type { LucideIcon } from 'lucide-react';

import { cn } from '../lib/cn.js';

import { Card, CardContent } from './card.js';
import { Skeleton } from './skeleton.js';

export interface StatCardProps {
  label: string;
  /** null triggers a skeleton (first load); a string renders the value. */
  value: string | null;
  icon?: LucideIcon;
  /**
   * Visual size of the numeric value. 'lg' (default) matches the original
   * DashboardPage KpiCard (36px); 'sm' matches the original
   * EventReportSection ReportCard (28px). Kept as a prop (rather than
   * forking the component) so both original call sites keep their exact
   * visual size after the parity migration.
   */
  size?: 'lg' | 'sm';
  className?: string;
}

/**
 * StatCard — shared KPI display card (cream tokens).
 *
 * Extracted from the duplicated `KpiCard` (apps/admin DashboardPage) and
 * `ReportCard` (apps/admin EventReportSection). See
 * admin-ticket-analytics-dashboard design D6.
 */
export function StatCard({ label, value, icon: Icon, size = 'lg', className }: StatCardProps) {
  const isLarge = size === 'lg';
  return (
    <Card
      className={cn(
        'border-jq-sand/70 bg-jq-surface-0 shadow-sm transition hover:shadow-md',
        className,
      )}
    >
      <CardContent className="p-5">
        <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-widest text-jq-graphite">
          {Icon ? <Icon className="h-3.5 w-3.5 text-jq-gold-deep" strokeWidth={1.75} /> : null}
          {label}
        </div>
        {value === null ? (
          <Skeleton className={cn('mt-3', isLarge ? 'h-9 w-24' : 'h-7 w-20')} />
        ) : (
          <p
            className={cn(
              'mt-3 font-editorial font-medium leading-none tracking-tight text-jq-navy tabular-nums',
              isLarge ? 'text-[36px]' : 'text-[28px]',
            )}
          >
            {value}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
